/**
 * A transient confirmation, bottom right — the app's toast, extracted.
 *
 * The Skills pane and the skill editor both reported "Restored to v3" and "Skill moved" as a
 * banner above the content. Two things were wrong with that (owner). It is at the top of the
 * page while the action was at the bottom of it, so the confirmation arrives where you are not
 * looking; and it takes a row of layout for a second and a half, which pushes everything below
 * it down and then back up.
 *
 * The markup is the plugins pane's toast, which is already this app's toast — `DiagnosticsFeedback`
 * borrowed the same classes before this component existed. It is here so that the next surface
 * that needs one does not borrow them a third time.
 *
 * **It does not dismiss itself.** The caller owns the timer, because the caller knows what the
 * message is about: the panes clear theirs after a couple of seconds, and an error that needs
 * reading can be left standing.
 */
import { useLocale } from "../i18n/LocaleProvider";

function OkIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12.5 2.5 2.5 4.5-5" />
    </svg>
  );
}

function ErrIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5M12 16.5v.01" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export function Toast({
  tone,
  text,
  onClose,
}: {
  tone: "ok" | "err";
  text: string;
  onClose: () => void;
}) {
  const { t } = useLocale();
  return (
    <div className="plugin-toast-stack">
      {/* `alert` for a failure, `status` for a confirmation: one interrupts a screen reader,
          the other waits its turn, and a successful move should not interrupt anything. */}
      <div className={`plugin-toast is-${tone}`} role={tone === "err" ? "alert" : "status"}>
        <span className="plugin-toast-icon" aria-hidden="true">{tone === "err" ? <ErrIcon /> : <OkIcon />}</span>
        <span className="plugin-toast-text">{text}</span>
        <button type="button" className="plugin-toast-close" aria-label={t("plugins.toast.dismiss")} onClick={onClose}>
          <CloseIcon />
        </button>
      </div>
    </div>
  );
}
